import React, { useId } from 'react';
import { getDealStatusDisplay } from '@/lib/api/myTenders';

/**
 * SVG status glyph — avoids Unicode geometric shapes (◐◔○) that disappear
 * once Inter loads (those codepoints are not in the webfont).
 */
export default function StatusSymbol({ status = null, size = 16, className = '' }) {
  const { color } = getDealStatusDisplay(status);
  const clipId = useId();
  const half = size / 2;
  const pad = 1.5;
  const r = half - pad;
  const box = size - pad * 2;
  const base = `shrink-0 block ${className}`;

  if (!status) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
        <circle cx={half} cy={half} r={r} fill="none" stroke={color} strokeWidth="1.5" />
      </svg>
    );
  }

  if (status === 'reviewing') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
        <defs>
          <clipPath id={clipId}>
            <rect x={half} y={0} width={half} height={half} />
          </clipPath>
        </defs>
        <circle cx={half} cy={half} r={r} fill="none" stroke={color} strokeWidth="1.5" />
        <circle cx={half} cy={half} r={r} fill={color} clipPath={`url(#${clipId})`} />
      </svg>
    );
  }

  if (status === 'pursuing') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
        <defs>
          <clipPath id={clipId}>
            <rect x={0} y={0} width={half} height={size} />
          </clipPath>
        </defs>
        <circle cx={half} cy={half} r={r} fill="none" stroke={color} strokeWidth="1.5" />
        <circle cx={half} cy={half} r={r} fill={color} clipPath={`url(#${clipId})`} />
      </svg>
    );
  }

  if (status === 'submitted') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
        <rect
          x={pad}
          y={pad}
          width={box}
          height={box}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          rx="1"
        />
        <rect x={half} y={pad} width={half - pad} height={half - pad} fill={color} />
      </svg>
    );
  }

  if (status === 'passed') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
        <circle cx={half} cy={half} r={r} fill={color} />
      </svg>
    );
  }

  if (status === 'won') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
        <path
          d={`M ${pad + 1} ${half} L ${half - 1} ${size - pad - 2} L ${size - pad} ${pad + 2}`}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === 'lost') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
        <path
          d={`M ${pad + 1} ${pad + 1} L ${size - pad - 1} ${size - pad - 1} M ${size - pad - 1} ${pad + 1} L ${pad + 1} ${size - pad - 1}`}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={base} aria-hidden>
      <circle cx={half} cy={half} r={r} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}
